import jsCookie from "js-cookie"
import { GraphQLClient } from "graphql-request"
import { getDeviceDate } from "@myvp/shared/src/clock/get-device-date"
import { parseJwt } from "@myvp/shared/src/functions/parse-jwt"
import { getDeviceProperties } from "@myvp/shared/src/functions/get-device-properties"
import { GLOBAL_DOMAIN } from "@myvp/shared/src/constants"
import { uuid } from "@myvp/shared/src/functions/uuid"
import { addMinutes } from "date-fns"
import { getRum } from "@myvp/shared/src/cloudwatch-RUM"
import { sanitizeGraphQLInputMiddleware } from "@myvp/shared/src/functions/sanitize-graphql-input"

/**
 * This is only meant to help with debugging logs to discern if the browser being used is the same.
 *
 * There is a scenario where a user cleared their cookies/browser history and kept using the same browser, so be mindful
 * when making assumptions using this variable.
 */
const firstTimeUsingThisDevice = (() => {
  const key = "first-used"

  const value = localStorage.getItem(key)

  if (!value) {
    const newValue = uuid().split("-")[0] + " " + getDeviceDate().toUTCString()
    localStorage.setItem(key, newValue)
    return newValue
  }

  return value
})()
/**
 * NOTE:
 *
 * When opening studio or study home from eCOA vault on localhost:
 * window.name is `open_window${Date.now()}`
 *
 * When NOT opening on localhost:
 * window.name is a uuid
 */
export const tabId = (() => {
  if (!window.name) {
    window.name =
      "myvp-" +
      // return the first part of the uuid to make it easier to read
      uuid().split("-")[0] +
      "-" +
      getDeviceDate().getTime()
  }
  return window.name
})()

function prepareHttpRequestHeaders(): Record<
  string,
  string | null | undefined
> {
  return {
    ...prepareVaultIdRequestHeaders(),
    ...prepareDevicePropertiesHeaders(),
    ...prepareStudyIdRequestHeaders(),
    ...prepareSiteIdRequestHeaders(),
    "myvp-tab-id": tabId,
    "myvp-first-used": firstTimeUsingThisDevice,
    ...prepareBearerHeader(),
    ...prepareFrscHttpRequestHeaders(),
  }
}

function processHttpResponseHeaders(headers: Headers) {
  // This specifically needs to happen BEFORE processJwtResponseHeaders
  processFrscResponseHeaders(headers)
  processJwtResponseHeaders(headers)
}

// bearer backwards
export const BEARER_KEY = "reraeb"
function prepareBearerHeader() {
  const bearer = jsCookie.get(BEARER_KEY)
  if (bearer) {
    return {
      Bearer: bearer,
    }
  }

  return {}
}
export const CSRF_KEY = "frsc"
function prepareFrscHttpRequestHeaders() {
  const cookieFrsc = jsCookie.get(CSRF_KEY)
  let frsc_k
  let frsc_t
  if (cookieFrsc) {
    // Example cookie is frsc_k=12765041342037374717.frsc_t=43763642014465558991
    const [frsc_k_s, frsc_t_s] = cookieFrsc.split(".")

    const [, frsc_k_value] = frsc_k_s.split("=")
    const [, frsc_t_value] = frsc_t_s.split("=")
    // Set values into localStorage so that in the event the request that called this
    // is cancelled by page refresh, subsequent network requests can use these values
    localStorage.setItem("frsc_k", frsc_k_value)
    localStorage.setItem("frsc_t", frsc_t_value)
    frsc_k = frsc_k_value
    frsc_t = frsc_t_value
  }
  if (!frsc_k && !frsc_t) {
    frsc_k = localStorage.getItem("frsc_k")
    frsc_t = localStorage.getItem("frsc_t")
  }
  return {
    "x-frsc-h": frsc_k,
    [frsc_k || "THIS_IS_HERE_TO_WORK_ON_IE11"]: frsc_t,
  }
}

function prepareVaultIdRequestHeaders() {
  const vaultId = sessionStorage.getItem("vaultId")
  if (vaultId) {
    return { vaultId }
  } else {
    return {}
  }
}

function prepareDevicePropertiesHeaders() {
  return {
    "device-properties": JSON.stringify(getDeviceProperties()),
  }
}

function prepareSiteIdRequestHeaders() {
  const globalStudySiteId = sessionStorage.getItem("globalStudySiteId")
  if (globalStudySiteId) {
    return {
      globalStudySiteId,
    }
  } else {
    return {}
  }
}
function prepareStudyIdRequestHeaders() {
  const globalSponsorStudyId = sessionStorage.getItem("globalSponsorStudyId")
  if (globalSponsorStudyId) {
    return { globalSponsorStudyId }
  } else {
    return {}
  }
}

function processFrscResponseHeaders(headers: Headers) {
  const frsc_t = headers.get("frsc_t")
  const frsc_k = headers.get("frsc_k")

  if (frsc_t && frsc_k) {
    jsCookie.set(CSRF_KEY, `frsc_k=${frsc_k}.frsc_t=${frsc_t}`, {
      /**
       * CSRF tokens are not valid for 60 minutes, but set an arbitrary long time so that they're not there forever.
       */
      expires: addMinutes(getDeviceDate(), 60),
      sameSite: "None",
      secure: true,
    })
    localStorage.setItem("frsc_t", frsc_t)
    localStorage.setItem("frsc_k", frsc_k)
  }
}

function processJwtResponseHeaders(headers: Headers) {
  const jwt = headers.get("x-atkn")
  if (jwt) {
    const { sesttl, ver, sub } = parseJwt(jwt)
    /**
     * At this point, we know that if x-atkn has been returned by a network request
     * that the csrf and ac cookies should be set. Therefore we should remove reraeb cookies + any global CSRF cookies that are set by the other domain.
     */

    jsCookie.remove(CSRF_KEY, { domain: GLOBAL_DOMAIN })
    jsCookie.remove(BEARER_KEY)
    // This cookie may have gotten set with or without a domain. So try to remove from both
    jsCookie.remove(BEARER_KEY, { domain: GLOBAL_DOMAIN })
    if (sesttl) {
      localStorage.setItem(ver ? "myvp-sesttl" : "myvp-psesttl", sesttl)
    }
    if (sub) {
      localStorage.setItem("myvp-sub", sub)
    }
  }
}

export const httpRequest = async (
  url: string | URL,
  requestInit: RequestInit = {}
) => {
  if (process.env.NODE_ENV === "test") {
    // oxlint-disable-next-line no-console
    console.error(
      `You failed to mock an http request inside of a unit test\n
       api: ${url} \n
       test: ${expect.getState().currentTestName}`
    )
    throw new Error("Failed to mock")
  }
  const headers = new Headers(requestInit.headers)
  for (const [key, value] of Object.entries(prepareHttpRequestHeaders())) {
    if (!headers.has(key)) {
      // NOTE: `as string` because the old code that was spreading the headers object
      // also effectively coerced `undefined` and `null` to string values in the headers.
      headers.set(key, value as string)
    }
  }
  const result = await fetch(url, {
    ...requestInit,
    headers,
  })

  processHttpResponseHeaders(result.headers)
  if (!result.ok) {
    // this will only work for non graphql requests since graphql returns a 200 with embedded error codes
    getRum().then((awsRum) => {
      if (awsRum) {
        const traceId = result.headers.get("x-trace-id")
        awsRum.logCustomEvent({
          name: `BACKEND_ERROR`,
          body: {
            status: result.status,
            url: result.url,
            traceId,
          },
        })
      }
    })
    throw result
  }
  return result
}

const isNestedErrorResponseHeaders = (
  response: object
): response is { response: Response } => {
  return Boolean(
    "response" in response &&
    response.response &&
    typeof response.response === "object" &&
    "headers" in response.response
  )
}

export const graphqlClient = new GraphQLClient("/api", {
  fetch: httpRequest,
  responseMiddleware: async (response) => {
    if (response instanceof Error) {
      let headers: Headers | undefined = undefined
      if ("headers" in response) {
        headers = response.headers as Headers
      } else if (isNestedErrorResponseHeaders(response)) {
        // When BE returns errors --> the response object comes back nested inside response
        headers = response.response.headers
      }
      if (headers) {
        getRum().then((awsRum) => {
          if (awsRum) {
            const traceId = headers.get("x-trace-id")
            awsRum.logCustomEvent({
              name: `BACKEND_ERROR`,
              body: { ...response, traceId },
            })
          }
        })
      }
    }
  },
  requestMiddleware: async (request) => {
    if (process.env.NODE_ENV === "test") {
      let operationName = ""
      try {
        operationName = JSON.parse(request.body as string).operationName
      } catch {}
      // oxlint-disable-next-line no-console
      console.error(
        `You failed to mock a graphql request inside of a unit test:\n
api: ${operationName}\n
test: ${expect.getState().currentTestName}`
      )
      // If this is happening but you're actually mocking the function, you likely have an expect at the end of the test ABOVE the failure
      // that is lacking an await waitFor at the end.
      throw new Error("Failed to mock")
    }

    // oxlint-disable-next-line no-param-reassign
    request = sanitizeGraphQLInputMiddleware(request)

    // oxlint-disable-next-line no-param-reassign
    request.headers = {
      ...request.headers,
      ...(request.operationName
        ? {
            // This is used for distributed tracing
            "X-GRAPHQL-OPERATION-NAME": request.operationName,
          }
        : {}),
    }
    return request
  },
})
